/**
 * @file DoctorLinkPanel.tsx
 * @brief Link VERBLIZR login to MEDYX doctor_Id/doctorKey for GRIP XML export.
 *
 * @details
 * ⚠️ The panel's own copy says "clinic software", not MEDYX — see
 * ClinicSoftwareSettingsPage's header. The field names stay as the connector
 * spells them, because that is what the doctor reads off their account.
 * (19-09-2026)
 *
 * @author DES
 * @date 02-09-2026
 */

import { useEffect, useState } from "react";
import { Link2, LoaderCircle, Search } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FormFieldGroup } from "@/components/ui/FormDialog";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { useAuth } from "@/shared/auth/auth-context";
import {
  useDoctorLink,
  useLookupDoctorLink,
  useUpdateDoctorLink,
} from "../api";

export function DoctorLinkPanel() {
  const { user } = useAuth();
  const doctorLink = useDoctorLink();
  const lookupDoctor = useLookupDoctorLink();
  const updateDoctor = useUpdateDoctorLink();
  const [doctorId, setDoctorId] = useState("");
  const [doctorKey, setDoctorKey] = useState("");
  const [lookupEmail, setLookupEmail] = useState(user?.email ?? "");

  useEffect(() => {
    if (doctorLink.data) {
      setDoctorId(
        doctorLink.data.doctorId != null ? String(doctorLink.data.doctorId) : "",
      );
      setDoctorKey(doctorLink.data.doctorKey ?? "");
    }
  }, [doctorLink.data]);

  useEffect(() => {
    if (user?.email) setLookupEmail(user.email);
  }, [user?.email]);

  const isLinked = Boolean(doctorLink.data?.doctorId || doctorLink.data?.doctorKey);

  const onLookup = async () => {
    try {
      const result = await lookupDoctor.mutateAsync({
        email: lookupEmail.trim() || undefined,
        mode: "fixture",
        autoLink: true,
      });
      toast({
        title: "Doctor record linked",
        description: `${result.doctor.name ?? "Doctor"} · ${result.doctor.doctorKey}`,
      });
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Lookup failed";
      toast({ title: "Doctor lookup failed", description: message });
    }
  };

  const onSaveManual = async () => {
    try {
      await updateDoctor.mutateAsync({
        doctorId: doctorId.trim() ? Number(doctorId) : null,
        doctorKey: doctorKey.trim() || null,
      });
      toast({ title: "Doctor link saved" });
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Save failed";
      toast({ title: "Could not save doctor link", description: message });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Link2 className="h-4 w-4" />
        <p className="text-sm font-medium">Clinic software doctor link</p>
        {isLinked ? (
          <Badge variant="secondary">Linked</Badge>
        ) : (
          <Badge variant="outline">Not linked</Badge>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        Links your login to the doctor ID and key your clinic software holds,
        so the GRIP XML export names you. Auto-links from Case Hub when the
        claim includes a doctor.
      </p>

      {isLinked ? (
        <p className="text-sm">
          {doctorLink.data?.name ? `${doctorLink.data.name} · ` : ""}
          ID {doctorLink.data?.doctorId ?? "—"} / key{" "}
          {doctorLink.data?.doctorKey ?? "—"}
        </p>
      ) : null}

      <div className="space-y-2">
        <FormFieldGroup label="Lookup by email" htmlFor="lookupEmail">
          <div className="flex flex-wrap gap-2">
            <Input
              id="lookupEmail"
              value={lookupEmail}
              onChange={(e) => setLookupEmail(e.target.value)}
              placeholder="doctor@example.com"
              className="max-w-sm"
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => void onLookup()}
              disabled={lookupDoctor.isPending}
            >
              {lookupDoctor.isPending ? (
                <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Search className="mr-2 h-4 w-4" />
              )}
              Lookup & link
            </Button>
          </div>
        </FormFieldGroup>
        <p className="text-xs text-muted-foreground">
          Fixture mode: use <code>doctor@example.com</code> for the sample demo
          doctor (101 / DR01).
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <FormFieldGroup label="Doctor ID" htmlFor="doctorId">
          <Input
            id="doctorId"
            value={doctorId}
            onChange={(e) => setDoctorId(e.target.value)}
            placeholder="e.g. 101"
          />
        </FormFieldGroup>
        <FormFieldGroup label="Doctor key" htmlFor="doctorKey">
          <Input
            id="doctorKey"
            value={doctorKey}
            onChange={(e) => setDoctorKey(e.target.value)}
            placeholder="e.g. DR01"
          />
        </FormFieldGroup>
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={() => void onSaveManual()}
        disabled={updateDoctor.isPending}
      >
        {updateDoctor.isPending ? (
          <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
        ) : null}
        Save manual link
      </Button>
    </div>
  );
}
